// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, SegmentedControl } from '@node-space/ui-components'
import {
  contactAtom,
  contactInstrumentActionAtom,
  contactInstrumentDecisionStepAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { Contact, ContactInstrumentDecisionStep, InstrumentType } from 'types/contacts'
import { ContactFiatInstrumentForm } from './components/FiatInstrument/FiatInstrument'
import { ContactDecision } from './ContactDecision'
import { ContactForm } from './ContactForm'
import { ContactInstrumentForm } from './ContactInstrumentForm'

export const ContactInstrumentDecision = () => {
  const { t } = useTranslation()

  const [isExistingContact, setIsExistingContact] = useState(false)
  const [accountType, setAccountType] = useState<InstrumentType>(InstrumentType.BANK_ACCOUNT)

  const setContact = useSetAtom(contactAtom)
  const step = useAtomValue(contactInstrumentDecisionStepAtom)
  const [contactHandleStep, setContactHandleStep] = useAtom(contactInstrumentDecisionStepAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)

  const showBack =
    contactHandleStep !== ContactInstrumentDecisionStep.CONTACT_DECISION &&
    !(contactHandleStep === ContactInstrumentDecisionStep.INSTRUMENT && !isExistingContact)

  const showSegmentedControl =
    step === ContactInstrumentDecisionStep.INSTRUMENT &&
    !contactInstrumentAction?.subHeader?.subTitle

  const getSheetHeader = (): string => {
    switch (contactHandleStep) {
      case ContactInstrumentDecisionStep.CONTACT:
        return t('contacts.addContact')
      case ContactInstrumentDecisionStep.CONTACT_DECISION:
        return t('contacts.existingContact')
      case ContactInstrumentDecisionStep.INSTRUMENT:
        return t('contacts.addBankAccountOrCryptoAddress')
      default:
        return ''
    }
  }

  const handleContactSelect = (contact: Contact) => {
    setContact(contact)
    setIsExistingContact(true)
    setContactHandleStep(ContactInstrumentDecisionStep.INSTRUMENT)
  }

  const handleOnBack = () => {
    if (contactHandleStep === ContactInstrumentDecisionStep.CONTACT) {
      setContactHandleStep(ContactInstrumentDecisionStep.CONTACT_DECISION)
    }
    if (contactHandleStep === ContactInstrumentDecisionStep.INSTRUMENT) {
      setIsExistingContact(false)
      setContactHandleStep(ContactInstrumentDecisionStep.CONTACT_DECISION)
    }
    setContact(null)
  }

  const handleTabChange = (value: string) => {
    setAccountType(value as InstrumentType)
    setContactInstrumentAction({
      ...contactInstrumentAction,
      onBack: undefined,
      subHeader: undefined,
    })
  }

  useEffect(() => {
    if (contactHandleStep !== ContactInstrumentDecisionStep.INSTRUMENT) {
      setAccountType(InstrumentType.BANK_ACCOUNT)
    }
  }, [contactHandleStep])

  useEffect(() => {
    setContactInstrumentAction({
      ...contactInstrumentAction,
      header: getSheetHeader(),
      subHeader: undefined,
      onClose: () => {
        contactInstrumentAction?.onClose()
        setContactHandleStep(ContactInstrumentDecisionStep.CONTACT_DECISION)
      },
      onBack: showBack ? handleOnBack : null,
      meta: {
        ...contactInstrumentAction?.meta,
        handleContactSelect,
      },
    })
  }, [contactHandleStep, accountType])

  return (
    <Box width="full">
      {step === ContactInstrumentDecisionStep.CONTACT_DECISION && (
        <ContactDecision setIsExistingContact={setIsExistingContact} />
      )}
      {step === ContactInstrumentDecisionStep.CONTACT && <ContactForm />}
      {step === ContactInstrumentDecisionStep.INSTRUMENT && (
        <>
          {showSegmentedControl && (
            <Box paddingT={24}>
              <SegmentedControl
                name="add-account-type"
                value={accountType}
                onChange={handleTabChange}
                aria-label={t('contacts.addBankAccountOrCryptoAddress')}
              >
                <SegmentedControl.Item
                  value={InstrumentType.BANK_ACCOUNT}
                  label={t('contacts.addBankAccount')}
                />
                <SegmentedControl.Item
                  value={InstrumentType.CRYPTO}
                  label={t('contacts.addCryptoAddress')}
                />
              </SegmentedControl>
            </Box>
          )}
          {accountType === InstrumentType.BANK_ACCOUNT ? (
            <ContactFiatInstrumentForm />
          ) : (
            <ContactInstrumentForm />
          )}
        </>
      )}
    </Box>
  )
}
